import React, { useCallback, useEffect, useState } from 'react';

// Manages persistent auto-apply rules (Layer 1) and manufacturer aliases
// (Layer 0). Rules can be created from the operator's current filter, simulated
// before enabling, toggled, and applied on demand.
export default function RulesManager({ api, currentPredicate, currentLabel, onError, onChanged }) {
  const [rules, setRules] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState({});
  const [ruleForm, setRuleForm] = useState({ name: '', action: 'approve', priority: 100, enabled: false });
  const [aliasForm, setAliasForm] = useState({ canonical_name: '', alias_name: '' });

  const fail = useCallback((e) => onError && onError(e.message || String(e)), [onError]);

  const reload = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.listRules(), api.listAliases()]);
      setRules(r.items || []);
      setAliases(a.items || []);
    } catch (e) {
      fail(e);
    }
  }, [api, fail]);

  useEffect(() => {
    reload();
  }, [reload]);

  const createRule = useCallback(async () => {
    if (!ruleForm.name.trim()) return fail(new Error('Rule name is required'));
    if (!currentPredicate) return fail(new Error('Set at least one filter to base the rule on'));
    setBusy(true);
    try {
      await api.createRule({ ...ruleForm, predicate: currentPredicate });
      setRuleForm({ name: '', action: 'approve', priority: 100, enabled: false });
      await reload();
      onChanged && onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, ruleForm, currentPredicate, reload, fail, onChanged]);

  const toggleRule = useCallback(async (rule) => {
    setBusy(true);
    try {
      await api.updateRule(rule.id, { enabled: !rule.enabled });
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, reload, fail]);

  const removeRule = useCallback(async (rule) => {
    setBusy(true);
    try {
      await api.deleteRule(rule.id);
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, reload, fail]);

  const simulate = useCallback(async (rule) => {
    try {
      const data = await api.simulateRule(rule.id);
      setSim((prev) => ({ ...prev, [rule.id]: data }));
    } catch (e) {
      fail(e);
    }
  }, [api, fail]);

  const applyNow = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.applyRulesNow();
      await reload();
      onChanged && onChanged();
      onError && onError(`Applied rules: ${res.approved} approved, ${res.rejected} rejected.`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, reload, onChanged, onError, fail]);

  const addAlias = useCallback(async () => {
    if (!aliasForm.canonical_name.trim() || !aliasForm.alias_name.trim()) {
      return fail(new Error('Both canonical and alias names are required'));
    }
    setBusy(true);
    try {
      await api.createAlias(aliasForm);
      setAliasForm({ canonical_name: '', alias_name: '' });
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, aliasForm, reload, fail]);

  const removeAlias = useCallback(async (alias) => {
    setBusy(true);
    try {
      await api.deleteAlias(alias.id);
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [api, reload, fail]);

  return (
    <div className="row g-3">
      {/* Rules */}
      <div className="col-12 col-xl-7">
        <div className="card h-100">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Auto-apply rules</span>
            <button className="btn btn-outline-primary btn-sm" onClick={applyNow} disabled={busy}>
              Apply rules now
            </button>
          </div>
          <div className="card-body">
            <p className="small text-muted">
              Rules run on every mapping run (and on demand). Reject rules drop matching candidates;
              approve rules promote them. New rules start disabled — simulate first, then enable.
            </p>

            <div className="border rounded p-2 mb-3 bg-light">
              <div className="small fw-semibold mb-1">New rule from current filter</div>
              <div className="small text-muted mb-2">
                Filter: <code>{currentLabel || '(set a filter above)'}</code>
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-5">
                  <input className="form-control form-control-sm" placeholder="Rule name"
                    value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
                </div>
                <div className="col-6 col-md-3">
                  <select className="form-select form-select-sm" value={ruleForm.action}
                    onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value })}>
                    <option value="approve">approve</option>
                    <option value="reject">reject</option>
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <input type="number" className="form-control form-control-sm" placeholder="priority"
                    value={ruleForm.priority} onChange={(e) => setRuleForm({ ...ruleForm, priority: Number(e.target.value) })} />
                </div>
                <div className="col-12 col-md-2 d-grid">
                  <button className="btn btn-primary btn-sm" onClick={createRule} disabled={busy || !currentPredicate}>
                    Save rule
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr><th>Name</th><th>Action</th><th>Prio</th><th>Applied</th><th>Enabled</th><th></th></tr>
                </thead>
                <tbody>
                  {rules.length === 0 && (
                    <tr><td colSpan="6" className="text-muted text-center">No rules yet.</td></tr>
                  )}
                  {rules.map((rule) => (
                    <tr key={rule.id}>
                      <td className="small">{rule.name}</td>
                      <td>
                        <span className={`badge ${rule.action === 'reject' ? 'text-bg-warning' : 'text-bg-primary'}`}>
                          {rule.action}
                        </span>
                      </td>
                      <td className="small">{rule.priority}</td>
                      <td className="small">
                        {rule.applied_count}
                        {sim[rule.id] && (
                          <span className="text-info ms-1" title="Would currently match">
                            (~{sim[rule.id].matched_entries})
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" checked={rule.enabled}
                            onChange={() => toggleRule(rule)} disabled={busy} />
                        </div>
                      </td>
                      <td className="text-end">
                        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => simulate(rule)} disabled={busy}>
                          Simulate
                        </button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => removeRule(rule)} disabled={busy}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Aliases */}
      <div className="col-12 col-xl-5">
        <div className="card h-100">
          <div className="card-header fw-semibold">Manufacturer aliases</div>
          <div className="card-body">
            <p className="small text-muted">
              Declare equivalent brand spellings so the scorer stops flagging them as a
              manufacturer mismatch (e.g. <code>ICP</code> → <code>International Comfort Products</code>).
              Takes effect on the next mapping run.
            </p>
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-5">
                <input className="form-control form-control-sm" placeholder="Alias (as seen)"
                  value={aliasForm.alias_name} onChange={(e) => setAliasForm({ ...aliasForm, alias_name: e.target.value })} />
              </div>
              <div className="col-12 col-md-5">
                <input className="form-control form-control-sm" placeholder="Canonical name"
                  value={aliasForm.canonical_name} onChange={(e) => setAliasForm({ ...aliasForm, canonical_name: e.target.value })} />
              </div>
              <div className="col-12 col-md-2 d-grid">
                <button className="btn btn-primary btn-sm" onClick={addAlias} disabled={busy}>Add</button>
              </div>
            </div>
            <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="table table-sm align-middle">
                <thead><tr><th>Alias</th><th>Canonical</th><th></th></tr></thead>
                <tbody>
                  {aliases.length === 0 && (
                    <tr><td colSpan="3" className="text-muted text-center">No aliases yet.</td></tr>
                  )}
                  {aliases.map((a) => (
                    <tr key={a.id}>
                      <td className="small">{a.alias_name}</td>
                      <td className="small">{a.canonical_name}</td>
                      <td className="text-end">
                        <button className="btn btn-outline-danger btn-sm" onClick={() => removeAlias(a)} disabled={busy}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
